import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type AxiosResponseHeaders, RawAxiosResponseHeaders,
} from 'axios'
import { computed, ref } from 'vue'
import { EnvironmentSettings } from '@shared/constants'
import { getLitiumRequestContext as getCurrentLitiumRequestContext } from '@shared/Services/litium-service'

type QueryParams = Record<string, unknown>

type UseApiOptions = {
  reloadOnAuthError?: boolean
}

type ApiErrorBody = {
  message?: string | string[]
  [key: string]: unknown
}

export const useApi = <TData = unknown, TErrorBody = ApiErrorBody>(
  endpoint: string,
  _access_token?: string,
  options?: UseApiOptions
) => {
  const api = axios.create({
    baseURL: EnvironmentSettings.ApiUrl,
    headers: {
      'litium-request-context': getLitiumRequestContextHeader(),
      RequestVerificationToken: window.localStorage.getItem('requestVerificationToken') ?? '',
    },
  })

  api.interceptors.request.use((config) => {
    const method = config.method?.toUpperCase() ?? 'GET'

    if (method !== 'GET') {
      const token = window.localStorage.getItem('requestVerificationToken')
      if (token) {
        config.headers.set('RequestVerificationToken', token)
      }
    }

    return config
  })

  api.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError<TErrorBody>) => {

      const status = error.response?.status
      if ((status === 401 || status === 403) && options?.reloadOnAuthError !== false) {
        window.location.reload()
      }
      return Promise.reject(error)
    }
  )

  const data = ref<TData | undefined>(undefined)
  const loading = ref<boolean>(false)
  const error = ref<AxiosError<TErrorBody> | undefined>(undefined)
  const headers = ref<AxiosResponseHeaders | RawAxiosResponseHeaders | undefined>(undefined)

  const run = async <TResult = TData>(
    request: Promise<AxiosResponse<TResult>>
  ): Promise<TResult> => {
    loading.value = true
    error.value = undefined

    try {
      const res = await request
      data.value = res.data as unknown as TData // keep `data` aligned with TData generic
      headers.value = res.headers
      return res.data
    } catch (e) {
      error.value = e as AxiosError<TErrorBody>
      throw e
    } finally {
      loading.value = false
    }
  }

  const get = <TResult = TData>(
    query?: QueryParams,
    config?: AxiosRequestConfig
  ): Promise<TResult> => {
    const queryString =
      query && Object.keys(query).length
        ? `?${Object.entries(query)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')}`
        : ''

    return run<TResult>(api.get<TResult>(endpoint + queryString, config))
  }

  const post = <TResult = TData, TPayload = unknown>(payload?: TPayload): Promise<TResult> =>
    run<TResult>(api.post<TResult>(endpoint, payload))

  const put = <TResult = TData, TPayload = unknown>(payload?: TPayload): Promise<TResult> =>
    run<TResult>(api.put<TResult>(endpoint, payload))

  const deleteRequest = <TResult = TData>(payload?: Record<string, unknown>): Promise<TResult> =>
    run<TResult>(api.delete<TResult>(endpoint, payload ? { data: payload } : undefined))

  const getFile = async (
    fileName: string,
    query?: QueryParams,
    config?: AxiosRequestConfig
  ): Promise<void> => {
    const blob = await get<Blob>(query, { ...config, responseType: 'blob' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    
    link.href = url
    link.style.display = 'none'
    link.download = fileName
    
    document.body.appendChild(link)
    
    link.click()
    link.remove()
    
    window.URL.revokeObjectURL(url)
  }

  const errorMessage = computed<TErrorBody | undefined>(() => error.value?.response?.data)

  const errorDetails = computed<string | string[] | undefined>(() => {
    const data = error.value?.response?.data as any
    return data?.message
  })

  const errorFields = computed<Record<string, string[]> | undefined>(() => {
    const msg = (error.value?.response?.data as any)?.message
    
    if (!Array.isArray(msg)) {
      return undefined
    }

    return (msg as string[]).reduce<Record<string, string[]>>((acc, m) => {
      let [field] = m.split(' ');
      (acc[field] ??= []).push(m)
      return acc
    }, {})
  })

  function getLitiumRequestContextHeader(): string {
    const ctx = getCurrentLitiumRequestContext()
    return ctx ? JSON.stringify(ctx) : ''
  }

  return {
    loading,
    data,
    error,
    headers,

    get,
    getFile,
    post,
    put,
    delete: deleteRequest,

    errorMessage,
    errorDetails,
    errorFields,
  }
}
